(ns dre.pages.detail.views
  (:require
   ["antd" :refer [Spin Popover]]
   ["moment" :as moment]
   [applied-science.js-interop :as j]
   [cuerdas.core :as str]
   [dre.app.events :as app-events]
   [dre.app.router :as router :refer [->route]]
   [dre.coll :refer [distinct-by provide-coll]]
   [dre.config :as config :refer [->api-url]]
   [dre.common.document-util :refer [guid->group-id]]
   [dre.components.beeswarm-plot :refer [beeswarm-plot]]
   [dre.components.collapse :refer [collapse]]
   [dre.components.dropdown :refer [dropdown dropdown-list]]
   [dre.components.icon :refer [svg fa-icon icon]]
   [dre.components.list :refer [collapsable-list]]
   [dre.components.loading-spinner :refer [loading-spinner]]
   [dre.components.search-results.annotations :refer [annotate-popover]]
   [dre.components.search-results.views.article :refer [tags-selector content-no-abstract content-no-abstract-nonclickable]]
   [dre.components.search-results.views.article-meta :refer [long-article-tags render-source-pills-for-sources-placeholder]]
   [dre.components.subject :as subject]
   [dre.components.switch :refer [switch]]
   [dre.components.tabs :refer [tabs]]
   [dre.components.tooltip :refer [tooltip-wrapper]]
   [dre.i18n.views :refer [tr]]
   [dre.i18n.events :as-alias i18n]
   [dre.modals.events :as-alias modals]
   [dre.panels.events :as-alias panels]
   [dre.pages.detail.events :as events]
   [dre.pages.detail.utils :refer [article-context-pills get-pico]]
   [dre.pages.my-searches.tags.events :as tag-events]
   [dre.pages.my-searches.tags.utils :refer [tag-pill]]
   [dre.pages.search.events :as search-events]
   [dre.components.search-results.events :as results-events]
   [dre.page-util :refer [dangerous unescape short-num]]
   [dre.util :refer [url-encode]]
   [dre.utils :refer [restricted-role? admin-role?]]
   [dre.utils.bem :as bem]
   [dre.shared.interface.names :refer [categories docsearch-tags]]
   [dre.widgets.authors.events :refer [author-capitalize]]
   [goog.dom :as gdom]
   [goog.dom.classlist :as gclasses]
   [goog.dom.TagName :as gtags]
   [goog.events :as gevents]
   [goog.events.EventType :as EventType]
   [goog.string :as gstring]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [reagent.dom :as rdom]
   [reagent.dom.server :as rds]))

(def component-name :article-details)

(defn article-title
  [article]
  (if (str/blank? (:article/title article))
    (:article/alt-title article)
    (:article/title article)))

;; API is sending us the classical fa-icon tag as <i class="fa fa-inbox"...> for
;; source pills and that it not handled because of the way we're using FontAwesome.
;; This is only used when the API returns classical fa-icon tags.
;;
;; TODO: Should be DELETED once the API stops sending classical fa-icon tags
(def fa-inbox-icon-html-str
  "<svg aria-hidden=\"true\" focusable=\"false\" data-prefix=\"fas\" data-icon=\"inbox\" class=\"svg-inline--fa fa-inbox\" role=\"img\" xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 512 512\"><path fill=\"currentColor\" d=\"M121 32C91.6 32 66 52 58.9 80.5L1.9 308.4C.6 313.5 0 318.7 0 323.9V416c0 35.3 28.7 64 64 64H448c35.3 0 64-28.7 64-64V323.9c0-5.2-.6-10.4-1.9-15.5l-57-227.9C446 52 420.4 32 391 32H121zm0 64H391l48 192H387.8c-12.1 0-23.2 6.8-28.6 17.7l-14.3 28.6c-5.4 10.8-16.5 17.7-28.6 17.7H195.8c-12.1 0-23.2-6.8-28.6-17.7l-14.3-28.6c-5.4-10.8-16.5-17.7-28.6-17.7H73L121 96z\"></path></svg>")

(defn google-scholar-uri
  [title]
  (str "https://scholar.google.com/scholar?hl=en&as_sdt=0%2C5&btnG=&q="
       (url-encode title)))

(defn rightfind-uri
  [query]
  (str "https://rightfind.copyright.com/rs-ui-web/search#journal/"
       (url-encode query)))

(defn pubmed-uri
  [pmid]
  (str "https://www.ncbi.nlm.nih.gov/pubmed/" pmid))

(defn format-related-uri
  [uri]
  (str/replace uri #"^http://www.who.int/trialsearch/Trial3.aspx\?trialid=" ""))

(defn decorate-ds-tags!
  [highlight-map]
  (let [decorate (fn [[id tags]]
                   (let [parent (gdom/getElement id)
                         classes (->> tags
                                      (map (partial str "ds-tag-"))
                                      (cons "ds-tag"))]
                     (when parent
                       (gclasses/addAll parent (clj->js classes)))))]
    (dorun
     (->> (vals highlight-map)
          (map (juxt :id :ds-tags))
          (filter (comp seq second))
          (map decorate)))))

(defn decorate-medcodes!
  [highlight-map vocabs codes]
  (let [select-codes (fn [codes]
                       (->> vocabs (select-keys codes) vals))
        sense->codes (fn [sense]
                       (->> sense (get codes) select-codes))
        senses->codes (fn [senses]
                        (->> senses (map sense->codes) (remove empty?) flatten))
        codes->str (fn [codes]
                     (->> (cycle [",\u2007" ",\u2007" "\n"])
                          (interleave codes)
                          butlast
                          (apply str)))
        decorate (fn [[id codes]]
                   (let [parent (gdom/getElement id)
                         children (gdom/getElementsByClass "medcodes" parent)]
                     (when parent (dorun (map gdom/removeNode children)))
                     (when parent (gclasses/remove parent "medcodes-ann"))
                     (when (and parent (seq codes))
                       (let [codes-str (str/replace (codes->str codes) "-" "\u2011")
                             child (gdom/createDom gtags/SPAN "medcodes" codes-str)]
                         (gevents/listen child EventType/CLICK #(doto % .preventDefault .stopPropagation))
                         (gclasses/add parent "medcodes-ann")
                         (gdom/appendChild parent child)))))]
    (dorun
     (->> (vals highlight-map)
          (map (juxt :id :senses))
          (map (fn [[id senses]]
                 [id (senses->codes senses)]))
          (map decorate)))))

(defn wrap-popovers
  [element subject-to-highlight sense-boldings-to-highlight-set search-query]
  (doseq [el (seq (.getElementsByClassName element "dre-ds-ann"))]
    (let [subject (unescape (.-href el))
          bold? (sense-boldings-to-highlight-set subject)
          meta (subject-to-highlight subject)
          subject (:top-subject meta)
          phrase (:phrase subject)
          inner (.-innerHTML el)
          _ (j/assoc! el :onclick (fn [e]
                                    (.stopPropagation e)
                                    (.preventDefault e)))]
      (rdom/render [annotate-popover (mapv #(list (:sense %) (:label %) (:is-suggestion %)) phrase) inner search-query bold?] el))))

(defn add-freetext-spans
  [mem freetext]
  (str/replace mem (re-pattern (str "(?i)" (str/join
                                            "( ?<[^>]*> ?| ?<\\/[^>]*> ?)?( ?<[^>]*> ?| ?<\\/[^>]*> ?)?( ?<[^>]*> ?| ?<\\/[^>]*> ?)?( ?<[^>]*> ?| ?<\\/[^>]*> ?)?"
                                            (map #(str "(" % ")") (str/split freetext " ")))))
               (fn [v]
                 (str/join "" (map-indexed (fn [idx val]
                                             (cond
                                               (or (= idx 0) (not val))
                                               ""
                                               (= (rem (- idx 1) 5) 0)
                                               (str "<strong>" val "</strong>")
                                               :else
                                               val)) v)))))

(defn xml-mount-and-decorate
  [article _selected-ds-tags medcodes selected-medcodes]
  (let [bold-senses (set @(rf/subscribe [::results-events/senses]))
        bold-free-text @(rf/subscribe [::results-events/free-text])
        search-query  @(rf/subscribe [::search-events/search-query])
        element (atom nil)
        {:keys [xml-str]} (:xml-annotated article)
        xml-str-bolded (reduce add-freetext-spans xml-str (reverse (sort-by count bold-free-text)))
        sense-boldings-to-highlight-set (set (map :subject-id (filter (fn [h]
                                                                        (let [[start end] (:ftspan h)
                                                                              senses (:senses h)]
                                                                          (if (some #(bold-senses %) senses)
                                                                            true
                                                                            false))) (get-in article [:xml-annotation :highlights]))))
        subject-to-highlight-map
        (into {}
              (map (fn [h] [(unescape (:subject-id h)) h]))
              (get-in article [:xml-annotation :highlights]))
        subject-to-highlight (fn [subject]
                               (get subject-to-highlight-map subject))
        tags-to-insert (rds/render-to-static-markup [long-article-tags article])
        sources-to-insert (rds/render-to-static-markup [render-source-pills-for-sources-placeholder article])]
    (r/create-class
     {:component-did-mount
      (fn [this]
        (let [links
              (array-seq (gdom/getElementsByTagNameAndClass "a" "dre-ds-ann" @element))

              related-subjects
              (reduce
               (fn [subject+elems l]
                 (let [subject (unescape (.-href l))]
                   (update subject+elems subject (fnil conj []) l)))
               {}
               links)

              tags-placeholder
              (gdom/getElement "tags-placeholder")

              sources-placeholder
              (gdom/getElement "sources-placeholder")

              classical-fa-icon
              (first (gdom/getElementsByTagNameAndClass "i" "fa-inbox"))]
          (wrap-popovers @element subject-to-highlight sense-boldings-to-highlight-set search-query)

          (doseq [el (seq (.getElementsByClassName @element "dre-ds-ann"))]
            (gevents/listen
             el
             #js [EventType/MOUSEOVER EventType/MOUSEOUT]
             (fn [e]
               (doseq [related-elem (get related-subjects (unescape (.-href (.-currentTarget e))))]
                 (gclasses/toggle related-elem "highlight")))))

          (when tags-placeholder
            (-> tags-placeholder
                (.-innerHTML)
                (set! tags-to-insert)))

          (when sources-placeholder
            (-> sources-placeholder
                (.-innerHTML)
                (set! sources-to-insert)))

          (when classical-fa-icon
            (-> classical-fa-icon
                (.-outerHTML)
                (set! fa-inbox-icon-html-str)))

          (decorate-ds-tags! subject-to-highlight-map)

          (decorate-medcodes! subject-to-highlight-map selected-medcodes medcodes)))

      :component-will-unmount
      #(doseq [el (seq (.getElementsByClassName @element "dre-ds-ann"))]
         (rdom/unmount-component-at-node el))

      :component-did-update
      (fn [this _]
        (let [[_ _ new-medcodes new-selected-medcodes] (rest (r/argv this))]
          (decorate-medcodes! subject-to-highlight-map new-selected-medcodes new-medcodes)))

      :reagent-render
      (fn [_article selected-ds-tags _medcodes _selected-medcodes]
        [:div.anntext
         {:ref #(reset! element %)
          :class (concat [:results-article-meta] (map (partial str "highlight-") selected-ds-tags))}
         (if xml-str-bolded
           (dangerous :div xml-str-bolded)
           [:div [tr "No annotation."]])])})))

(defn add-tab-params
  [{:keys [:article/source :article/uri :article/guid]}]
  {:article/source source
   :article/uri uri
   :article/guid guid
   :id guid
   :label (str
           (get categories source source)
           " / " (format-related-uri uri))})

(defn group-tab
  [article selected-tab-id]
  (let [guid (:article/guid article)
        groups (:article/groups article)
        group-id (guid->group-id guid groups)
        tabs-definition (->> (get groups group-id)
                             (map add-tab-params)
                             (sort-by :label))
        [head rest] (split-at 3 tabs-definition)
        related-base (or
                      (:related-base article)
                      guid)
        change-tab (fn [id]
                     (let []
                       (if (or (= id @selected-tab-id) (= id :rest))
                         nil
                         (rf/dispatch [::router/redirect-to-path-with-query-params (->route :detail {:id id}) (when (not= id related-base)
                                                                                                                {:related-base
                                                                                                                 related-base})]))))
        rest-drop (if (seq rest)
                    [dropdown
                     {:alignment :right
                      :anchor
                      [:button
                       {:class [(bem/bm :button :bare) (bem/bm :button :small)]}
                       [svg :common :chevron-down]]
                      :content
                      [dropdown-list
                       (mapv (fn [e] [:span
                                      {:on-click (fn [] (change-tab (:id e)))}
                                      (:label e)]) rest)]}]
                    nil)]
    (when tabs-definition
      [:<>
       [tabs {:items head
              :on-click (fn [item] (change-tab (:id item)))
              :default-active-id @selected-tab-id}]
       rest-drop])))

(defn- tag-content
  [_article _selected-articles _tags _utags _all-tags-filtered _user]
  (let [open? (r/atom false)
        close-fn #(reset! open? false)]
    (fn [article selected-articles tags utags all-tags-filtered user]
      [:> Popover
       {:content (r/as-element [:div
                                (doall
                                 (for [t tags]
                                   ^{:key (:tag_uuid t)}
                                   [tag-pill t user]))])
        :trigger :hover}
       [:> Popover
        {:content (r/as-element [tags-selector article selected-articles utags all-tags-filtered user close-fn [article]])
         :trigger :click
         :open @open?
         :on-open-change (fn [v] (reset! open? v))}
        [:button
         {:class [(bem/bm :button :medium)]}
         [svg :common :tag]]]])))

(defn- tag-empty-content
  [_article _selected-articles _utags _all-tags-filtered _user]
  (let [open? (r/atom false)
        close-fn #(reset! open? false)]
    (fn [article selected-articles utags all-tags-filtered user]
      [tooltip-wrapper "Tag article with your own custom label."
       [:> Popover
        {:content (r/as-element [tags-selector article selected-articles utags all-tags-filtered user close-fn [article]])
         :trigger :click
         :open @open?
         :on-open-change (fn [v] (reset! open? v))}
        [:button
         {:class [(bem/bm :button :medium)]}
         [svg :common :tag-empty]]]])))

(defn sidebar-navigation
  [article oa-url]
  (let [;;start tags---
        article-tags {:source (:article/source article)
                      :uri (:article/uri article)
                      :guid (:article/guid article)}
        search-state @(rf/subscribe [::search-events/state])
        tags-loading? @(rf/subscribe [::tag-events/loading?])
        all-tags @(rf/subscribe [::tag-events/tags-without-filter])
        all-tags-filtered @(rf/subscribe [::tag-events/tags])
        tag_uuids (set (map :tag_uuid all-tags))
        utags (get search-state :articles-utags)
        tags (get utags [(:source article-tags)
                         (:uri article-tags)])
        ;; consistenty check
        tags (seq (distinct-by :tag_uuid (filter #(contains? tag_uuids (:tag_uuid %)) tags)))
        ;;end tags---
        last-seen-article (:signal/last-seen-article (:signal search-state))
        new? (and last-seen-article (> (:article/serial article) last-seen-article))
        user @(rf/subscribe [::app-events/user])
        app-db @(rf/subscribe [::app-events/db])
        doi (get-in article [:article/meta :doi])
        oa? (get-in oa-url [:oadoi :meta :is_oa] false)
        oa-link (or
                 (get-in oa-url [:oadoi :url])
                 (get-in oa-url [:crossref :url]))
        disable-oa-link? (or (not doi) (not oa-link))
        issn (first (provide-coll (get-in article [:article/meta :journal :issn])))]
    [:<>
     (when-not (restricted-role? user)
       (if tags-loading?
         [:span
          {:class (bem/be component-name :col-tag-icon)}
          [loading-spinner]]
         (if tags
           [tag-content article-tags nil tags utags all-tags-filtered user]
           [tag-empty-content article-tags nil utags all-tags-filtered user])))

     (when-not (str/blank? issn)
       [tooltip-wrapper "Search RightFind" [:button
                                            {:class [(bem/bm :button :medium)]}
                                            [:a
                                             {:style {:cursor "pointer"}
                                              :href (rightfind-uri issn)
                                              :target "_blank"
                                              :rel "noopener noreferrer"}
                                             [:b "RF"]
                                             #_[svg :common :google-scholar]]]])
     [tooltip-wrapper "Search Google Scholar" [:button
                                               {:class [(bem/bm :button :medium)]}
                                               [:a
                                                {:style {:cursor "pointer"}
                                                 :href (google-scholar-uri (article-title article))
                                                 :target "_blank"
                                                 :rel "noopener noreferrer"}
                                                [svg :common :google-scholar]]]]
     (when (= "medline" (:article/source article))
       [tooltip-wrapper "Open in PubMed" [:button
                                          {:class [(bem/bm :button :medium)]}
                                          [:a
                                           {:style {:cursor "pointer"}
                                            :href (pubmed-uri (:article/uri article))
                                            :target "_blank"
                                            :rel "noopener noreferrer"}
                                           [svg :common :pubmed]]]])
     [tooltip-wrapper (cond
                        disable-oa-link?
                        "OpenAccess Unavailable"
                        oa?
                        "OpenAccess Available"
                        (not disable-oa-link?)
                        "OpenAccess Uncertain")
      [:button
       {:class [(bem/bm :button :medium)
                (when disable-oa-link? "button--disabled")]}
       [:a
        {:style {:cursor "pointer" :text-shadow (when (and (not disable-oa-link?) oa?) "0 0 1px #34ff00")}
         :href (when-not disable-oa-link? oa-link)
         :target "_blank"
         :rel "noopener noreferrer"}
        [svg :common (if (and (not disable-oa-link?) oa?) :open-access-green :open-access)]]]]
     [tooltip-wrapper "Download" [:button
                                  {:class [(bem/bm :button :medium)]
                                   :on-click #(rf/dispatch [::modals/set-modal :search.export/export {:single-article? true
                                                                                                      :selected-article article}])}
                                  [svg :common :export]]]
     (when (admin-role? user)
       [tooltip-wrapper "Source" [:button
                                  {:class [(bem/bm :button :medium)]}
                                  [:a
                                   {:style {:cursor "pointer"}
                                    :href (str (->api-url app-db) "/api/source/" (:article/guid article))
                                    :target "_blank"
                                    :rel "noopener noreferrer"}
                                   "</>"]]])
     (when new?
       [:div {:class :results-article}
        [:div
         {:class [:new-item (bem/be :results-article :new)]}
         [tr "NEW"]]])]))

(defn person-li
  [article-pills person]
  (let [{:keys [:author/name] :as person} person
        person-link [:a
                     {:on-click #(rf/dispatch [::panels/set-panel :author {:params {:author/author person} :search-query article-pills}])}
                     (author-capitalize name)]]
    [:li.person.title
     (if (seq (:author/affiliations person))
       [tooltip-wrapper
        (r/as-element [:span (for [aff (:author/affiliations person)]
                               ^{:key (hash (:affiliation/name aff))}
                               [:div (:affiliation/name aff)])])
        person-link
        {:placement "bottom"}]
       person-link)]))

(defn persons
  [_article]
  (let [more? (r/atom false)]
    (fn [article]
      (let [article-pills (article-context-pills article)
            {:keys [:article/persons]} article
            last-person (last persons)
            [head rest] (split-at 3 (drop-last persons))]
        (when (seq persons)
          [:h3
           [:ul.list-inline.commas
            {:style {:display :flex
                     :flex-wrap :wrap}} ;; Fix for jumpy tooltips
            (for [person head]
              ^{:key (hash person)}
              [person-li article-pills person])
            (when @more?
              (for [person rest]
                ^{:key (hash person)}
                [person-li article-pills person]))
            (when (seq rest)
              [:li.more
               [:a.clickable
                {:on-click #(swap! more? not)}
                (if @more?
                  [:span [fa-icon :chevron-circle-left]]
                  [:span " … " [:span {:style {:font-size "1rem"}} "(" (count rest) ")"] " "])] ","])
            [person-li article-pills last-person]]])))))

(defn pico-block
  [article key label icon-key]
  (let [pico (get-pico article key)
        pico-class @(rf/subscribe [::events/pico-class key])]
    (when (seq pico)
      [:div
       {:class (bem/be component-name :right-side-block)}
       [collapse
        [{:label (r/as-element [:b {:class [:right-side-block-title (bem/bm :right-side-block-title pico-class) key]
                                    :on-click (fn [e] (.stopPropagation e))}
                                [:span
                                 {:on-click #(rf/dispatch [::events/cycle-pico key])}
                                 [icon :widgets icon-key]]
                                [:span label]])
          :children (r/as-element [:div
                                   {:class :right-side-block-content}
                                   [collapsable-list
                                    (mapv (fn [{:keys [subject-id phrase]}]
                                            {:content (r/as-element [:span
                                                                     [subject/phrase
                                                                      phrase
                                                                      nil]])}) (distinct-by :phrase pico))
                                    -1]])}]
        :all]])))

(defn ds-tags-block
  [article selected-ds-tags]
  (let [highlights (:highlights (:xml-annotation article))
        tag-freq (->> highlights (map :ds-tags) flatten frequencies)
        tags (->> docsearch-tags
                  (map (fn [[tag label]]
                         [tag label (get tag-freq tag 0)]))
                  (sort-by last)
                  reverse)]
    [:div
     {:class (bem/be component-name :right-side-block)}
     [collapse
      [{:label (r/as-element [:b [tr "Semantic Type Annotations"]])
        :children (r/as-element [:div
                                 {:class [:right-side-block-content (bem/be component-name :ds-tags-block)]}
                                 [collapsable-list
                                  (mapv (fn [[tag label tag-count]]
                                          (let [disabled? (zero? tag-count)
                                                checked? (contains? selected-ds-tags tag)]
                                            {:content (r/as-element [:div
                                                                     {:class [:ds-tag-label (when disabled? :disabled) (str "highlight-" tag)]
                                                                      :on-click (when (not disabled?)
                                                                                  #(do (rf/dispatch [::events/toggle-ds-tag tag])
                                                                                       (rf/dispatch [::events/dim-picos])))}
                                                                     [:div
                                                                      {:class [(str "ds-tag-" tag) :ds-tags-color-dot]}]
                                                                     label])
                                             :actions [(when checked?
                                                         (r/as-element [:span
                                                                        {:class [:ds-tag-check-icon (when disabled? :disabled)]}
                                                                        [fa-icon :check]]))
                                                       (r/as-element [:span
                                                                      {:class [:ds-tags-count (when disabled? :disabled)]}
                                                                      tag-count])]})) tags)
                                  -1]])}]
      :none]]))

(defn vocab->codes
  [vocab codes]
  (->> (vals codes)
       (map #(get % vocab))
       (remove nil?)
       (apply concat)
       set))

(defn medcodes-block
  [vocabs codes]
  [:div
   {:class (bem/be component-name :right-side-block)}
   [collapse
    [{:label (r/as-element [:b [tr "Medical Codes"]])
      :children (r/as-element [:div
                               {:class [:right-side-block-content (bem/be component-name :medcodes-block)]}
                               [collapsable-list
                                (mapv (fn [vocab]
                                        (let [label (str (events/medcodes-vocab->label vocab) " Code")
                                              codes-count (count (vocab->codes vocab codes))
                                              disabled? (zero? codes-count)
                                              checked? (contains? vocabs vocab)]
                                          {:content (r/as-element [:div
                                                                   {:class [:medcodes-label (when disabled? :disabled)]
                                                                    :on-click (when (not disabled?)
                                                                                #(do (rf/dispatch [::events/toggle-medcodes vocab])
                                                                                     (rf/dispatch [::events/dim-picos])))}
                                                                   label])
                                           :actions [(when checked?
                                                       (r/as-element [:span
                                                                      {:class [:medcodes-check-icon (when disabled? :disabled)]}
                                                                      [fa-icon :check]]))
                                                     (r/as-element [:span
                                                                    {:class [:medcodes-count (when disabled? :disabled)]}
                                                                    codes-count])]})) (sort events/tracked-medcodes-vocabs))
                                -1]])}]
    :all]])

(defn tag-pills
  [article]
  (let [article-tags {:source (:article/source article)
                      :uri (:article/uri article)
                      :guid (:article/guid article)}
        search-state @(rf/subscribe [::search-events/state])
        ;; tags-loading? @(rf/subscribe [::tag-events/loading?])
        all-tags @(rf/subscribe [::tag-events/tags-without-filter])
        tag_uuids (set (map :tag_uuid all-tags))
        utags (get search-state :articles-utags)
        tags (get utags [(:source article-tags)
                         (:uri article-tags)])
        ;; consistenty check
        tags (seq (distinct-by :tag_uuid (filter #(contains? tag_uuids (:tag_uuid %)) tags)))]
    [:div
     {:style {:margin-bottom ".3em"}}
     (doall
      (for [t tags]
        ^{:key (:tag_uuid t)}
        [tag-pill t]))]))

(defn linked-references-block
  []
  (let
   [show-lines @(rf/subscribe [::events/beeswarm-show-lines])
    plot-data @(rf/subscribe [::events/beeswarm-plot format-related-uri])]
    [collapse
     [{:label (r/as-element [:b [tr "Linked References"]])
       :children (r/as-element [beeswarm-plot
                                (assoc
                                 plot-data
                                 :label-fn
                                 (fn [item]
                                   (if (:target/group item)
                                     (gstring/format "Title: %s\n\nID: %s\n\nPublished: %s\n\nGroup: %s"
                                                     (:target/title item)
                                                     (str
                                                      (:target/source item)
                                                      "/"
                                                      (:target/uri item))
                                                     (.format
                                                      (moment (:target/published item))
                                                      "YYYY-MM-DD")
                                                     (:target/group-str item))
                                     (gstring/format "Title: %s\n\nID: %s\n\nPublished: %s"
                                                     (:target/title item)
                                                     (str
                                                      (:target/source item)
                                                      "/"
                                                      (format-related-uri (:target/uri item)))
                                                     (.format
                                                      (moment (:target/published item))
                                                      "YYYY-MM-DD"))))
                                 :value-fn :target/published)])
       :extra (r/as-element [tooltip-wrapper "Toggle lines" [:span
                                                             [switch
                                                              {:value show-lines
                                                               :on-change (fn [_ e]
                                                                            (.stopPropagation e)
                                                                            (rf/dispatch [::events/toggle-beeswarm-lines]))}]]])}]
     :all]))

(defn recommendations-block
  [loading? data]
  (let
   []
    [collapse
     [{:label (r/as-element [:b [tr "Related articles"]])
       :children (r/as-element (if loading?
                                 [:div
                                  {:class (bem/be component-name :loading)}
                                  [:> Spin]]
                                 [:div
                                  {:class :recommendations_content}
                                  [collapsable-list
                                   (mapv (fn [article]
                                           {:content (r/as-element [:div
                                                                    [content-no-abstract {:title (article-title article)
                                                                                          :category (:article/category article)
                                                                                          :meta (:article/meta article)
                                                                                          :source (:article/source article)
                                                                                          :uri (:article/uri article)
                                                                                          :persons (update-keys
                                                                                                    (:article/persons article)
                                                                                                    keyword)
                                                                                          :guid  (:article/guid article)
                                                                                          :published (:article/published article)}]])})
                                         data)
                                   -1]]))}]
     :all]))


(defn article-content
  [article pico-classes selected-ds-tags medcodes selected-medcodes]
  (let [user @(rf/subscribe [::app-events/user])]
    [:article
     {:class (concat pico-classes [(bem/be component-name :annotations)])}
     (dangerous :h1
                {:class (bem/be component-name :article-title-truncated-5)
                 :on-mouse-enter (fn [e]
                                   (let [is-ellipsis-active? (< (+ 1 (.-clientHeight (.-currentTarget e))) (.-scrollHeight (.-currentTarget e)))] ;;;1px fix for firefox
                                     (if is-ellipsis-active?
                                       (set! (. (.-target e) -title) (article-title article))
                                       (set! (. (.-target e) -title) ""))))}
                (article-title article))
     [:div
      {:class (bem/be component-name :persons)}
      [persons article]]
     (when-not (restricted-role? user)
       [tag-pills article])
     [:section.text
      [xml-mount-and-decorate article selected-ds-tags medcodes selected-medcodes]]]))

(defn prev-next-control
  [article key idx cfn count loading?]
  (let [title [content-no-abstract-nonclickable article]
        arrow (str (case key
                     :prev "< "
                     :next " >"))]
    [tooltip-wrapper
     (r/as-element [:div.prev-next-tooltip title])
     (if loading?
       [:div.prev-next-item [:> Spin]]
       [:div.prev-next-item.prev-next-button
        {:on-click
         #(do
            (rf/dispatch-sync [::router/redirect-to-path (->route :detail {:id (:guid article)})])
            (rf/dispatch-sync [::results-events/set-clicked-article (cfn idx) [::events/optimistic-prev-next]]))}
        (when (= :prev key)
          arrow)
        [:span (str (cfn idx) " of " (short-num count))]
        (when (= :next key)
          arrow)])]))

(defn prev-next-controls
  []
  (let [prev-next @(rf/subscribe [::events/prev-next])
        {:keys [:cursor
                :idx
                :loading?
                :total]} prev-next
        items-count total
        [prev next] cursor
        has-prev? (and
                   (pos? items-count)
                   (> idx 1))
        has-next? (and
                   (pos? items-count)
                   (> items-count idx))]
    (if loading?
      [:div
       {:class (bem/be component-name :prev-next)
        :style {:justify-content "center"}}
       [:> Spin]]
      (when (or has-next? has-prev?)
        [:div
         {:class (bem/be component-name :prev-next)}
         (if has-prev?
           [prev-next-control prev :prev idx dec items-count loading?]
           [:div.cursor-controls__placeholder])
         [:div.prev-next-item (str idx " of " (short-num items-count))]
         (if has-next?
           [prev-next-control next :next idx inc items-count loading?]
           [:div.cursor-controls__placeholder])]))))

(defn view
  []
  (let [loading? @(rf/subscribe [::events/loading?])
        loading-recommendations? @(rf/subscribe [::events/loading-recommendations?])
        data @(rf/subscribe [::events/data])
        article (:article data)
        show-relations? (> (count (:article/relations article)) 1)
        pico-classes @(rf/subscribe [::events/pico-classes])
        selected-ds-tags @(rf/subscribe [::events/selected-ds-tags])
        medcodes @(rf/subscribe [::events/medcodes])
        selected-medcodes @(rf/subscribe [::events/selected-medcodes])
        guid (:article/guid article)
        selected-tab-id (r/atom guid)]
    [:div
     {:class component-name}
     (if loading?
       [:div
        {:class (bem/be component-name :loading)}
        [:> Spin]]
       [:div
        {:class (bem/be component-name :loaded)}
        [:div
         {:class (bem/be component-name :left-side)}
         [:<>
          [prev-next-controls]
          (when show-relations?
            [:div
             {:class (bem/be component-name :linked-references)}
             [linked-references-block]])
          [:div
           {:class (bem/be component-name :group-tab)}
           [group-tab article selected-tab-id]]
          [:div
           {:class (bem/be component-name :sidebar-navigation)}
           [sidebar-navigation article (:oa-url data)]]
          [:div
           {:class (bem/be component-name :article)}
           [article-content article pico-classes selected-ds-tags medcodes selected-medcodes]]
          [:div
           {:class (bem/be component-name :recommendations)}
           [recommendations-block loading-recommendations? (:recommendations data)]]]]
        [:div
         {:class (bem/be component-name :right-side)}
         [:<>
          [pico-block article :intervention [tr "Interventions"] :interventions]
          [pico-block article :outcome [tr "Outcomes"] :outcomes]
          [pico-block article :characteristic [tr "Characteristics"] :characteristics]
          [medcodes-block selected-medcodes medcodes]
          [ds-tags-block article selected-ds-tags]]]])]))
